import { ecLean, itemField } from '../janus-engine/ability/context';
import { cachedInventoryRow } from '../janus-engine/inventory/cached-inventory-trees';
import { inventoryField } from '../shared/array-types';
import { logParticipantSnapshot } from '../shared/log-participant-snapshot';
import { peekAudioCatalog, type RollSound } from '../shared/audio-catalog';
import { abilityVfxUsesAreaCast, captureCommandRollVfx, isResolvedRollVfxType, normalizeRollVfx, resolveRollVfxRecipe, rollPresentationKey } from '../janus-engine/presentation/roll-vfx-cue';
import abilityMotion from '../../../ft3d/creator/data/avatar-clips/ability-motion-v1.json';
export { localAttackRolls } from '../janus-engine/presentation/attack-roll-rows';
import type { WSMessage } from './types';

type Row = Record<string, unknown>;
type RollInsert = Extract<WSMessage, { type: 'roll_insert' }>;
const row = (value: unknown): Row => value && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const text = (value: unknown): string => String(value ?? '');
// Negative, page-wide serials cannot collide with persisted roll_i values or a replacement local sink.
const serial = (globalThis as { __ftLocalRollSerial?: { value: number } }).__ftLocalRollSerial ??= { value: 0 };
const presentationOffsets = new WeakMap<Row, Record<string, number>>();

/** Local counterpart of the commit's dedicated area/movement relays; no dice, log row or sound. */
export function presentLocalAbilityVfx(command: Row, held: (id: string) => Row | undefined): void {
    captureCommandRollVfx(command, held, (window as unknown as { ROLLTYPE_CATALOG?: Record<string, {item_id?: string}> }).ROLLTYPE_CATALOG ?? {});
    const verdict = row(command.verdict), source = text(command.actor_id).toUpperCase(), encounterId = text(command.encounter_id).toUpperCase();
    const publish = (type: string, itemId: string, target: string, cue: Row) => document.dispatchEvent(new CustomEvent(type, { detail: { roll: {
        roll_id: command.operation_id, roll_vfx_action_id: text(command.operation_id).toUpperCase(), roll_item_id: itemId, roll_by: source, roll_to: target, encounter_id: encounterId,
        roll_private: command.roll_private ?? 0, roll_vfx: { schema: 'ft.ft3d.magic-cue.v1', itemId, encounterId,
            sourceEntityId: source, targetEntityId: target, ...cue },
    } } }));
    if (Array.isArray(verdict.areaCasts)) verdict.areaCasts.forEach(value => {
        const cast = row(value), itemId = text(cast.itemId);
        publish('ft-magic-area-authorized', itemId, source, row(cast.cue));
    });
    const itemId = text(command.ability_id), recipe = row(row(held(itemId)?.assets).magic_vfx);
    if (recipe.projectileStyle === 'purchased-wind-push' && Array.isArray(verdict.forcedMovements)) {
        for (const value of verdict.forcedMovements) {
            const movement = row(value), target = text(movement.targetId);
            publish('ft-magic-target-authorized', itemId, target, { id: `${command.operation_id}:${itemId}:${source}:movement:${target}`,
                recipe, movement: { schema: 'ft.ft3d.forced-movement-result.v1', rollId: command.operation_id, ...movement } });
        }
    }
}

/** turn_truth_commit_inc.php: only public, PHP-shaped end/expire picks claim turn rows. */
function turnSoundClaimed(command: Row, kind: string): boolean {
    const sound = row(command.sound);
    if (Number(command.roll_private ?? 0) !== 0 || sound.v !== 1 || !Array.isArray(sound.picks)) return false;
    // ft_client_action_sound slices before filtering and requires complete slot/UUID matches (PHP's /D).
    return sound.picks.slice(0, 8).some(value => {
        const pick = row(value), slot = typeof pick.slot === 'string' ? pick.slot : '', id = typeof pick.audio_id === 'string' ? pick.audio_id : '';
        if (slot.match(/^[a-z][a-z0-9_:]{0,31}$/)?.[0] !== slot
            || id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)?.[0] !== id) return false;
        switch (slot.split(':')[0]) {
            case 'end': return ['end_turn', 'start_turn'].includes(kind.toLowerCase());
            case 'expire': return ['status_effect_expired', 'effect_ends_on_target_end_of_turn', 'effect_ends_on_target_start_of_turn'].includes(kind.toLowerCase());
            default: return false;
        }
    });
}

/** ft_roll_sound's reference, from held presentation identity/revision and the action's already-picked slots. */
function localRollSound(input: Row, command: Row, roll: Row, used: Row | undefined): RollSound | null {
    if (Number(roll.roll_private) === 2) return null;
    if (command.schema === 'janus.turn.end.v1' && turnSoundClaimed(command, text(roll.roll_type_1))) return null;
    const sound: RollSound = { v: 2, ...row(input.roll_sound) };
    const rev = peekAudioCatalog()?.rev;
    if (rev && !sound.rev) sound.rev = rev;
    if (command.schema === 'janus.roll.truth.v1') {
        // roll_truth_commit passes type/name only for dice/table, and no used item for complex.
        if (command.kind === 'complex') sound.used = 0;
    } else {
        const usedId = text(used && itemField(used, 'item_id')).toUpperCase();
        if (!roll.roll_item_id) sound.used = 0;
        else if (usedId && usedId !== text(roll.roll_item_id).toUpperCase()) sound.used = usedId;
    }
    if (command.schema === 'janus.action.ability.v2'
        && (roll.roll_type_1 === 'orb_channeled' || roll.roll_to === command.encounter_id)) sound.voice = 0;
    if (command.schema === 'janus.action.attack.v2') {
        const rolls = row(command.rolls), kind = text(roll.roll_type_1), item = text(roll.roll_item_id);
        const damage = [row(rolls.damage), row(row(rolls.rider).damage)].some(entry => entry.roll_type_1 === kind);
        const death = ['creature_killed', 'creature_killed_forever', 'massive_damage_instant_death'].includes(kind);
        if (Number(command.roll_private ?? 0) === 0) {
            const picks = row(command.sound).picks;
            const slots = new Set(Array.isArray(picks) ? picks.map(pick => text(row(pick).slot).split(':')[0]) : []);
            if (slots.has('attack') && kind === row(rolls.attack).roll_type_1
                || slots.has('hit') && damage || slots.has('whiff') && kind === 'weapon_attack_missed'
                || slots.has('reaction') && kind === 'reaction_ability_applied' && item === row(rolls.reaction).ability_id
                || slots.has('death') && death) return null;
        }
        if (text(roll.roll_by).toUpperCase() === text(command.actor_id).toUpperCase()
            || damage || kind === 'weapon_attack_missed' || death) sound.voice = 0;
    }
    return sound;
}

/** Presentation snapshots only: consume the already-decided insert_roll inputs and the held roster/catalog. */
export function localRollInsert(input: Row, command: Row, encounterId: string, held: (id: string) => Row | undefined,
    vfxRows: readonly Row[] = []): RollInsert | null {
    const kind = text(input.roll_type_1 || (input.kind === 'complex' ? 'complex_roll' : ''));
    if (!kind) return null; // ability telemetry is retained in sink.logs, but is not a roll.
    const by = text(input.roll_by || command.actor_id || command.caster_id);
    const to = text(input.roll_to ?? command.target_id);
    const itemId = text(input.roll_item_id ?? command.weapon_id ?? command.ability_id);
    const participant = (id: string) => {
        const source = held(id), snapshot = row(row(command.log_participants)[id.toUpperCase()]);
        const identity = source ? logParticipantSnapshot(source) : {};
        return { item_id: id, item_type: text(source && itemField(source, 'item_type')),
            item_name: snapshot.name ?? identity.name ?? '', pic_url: [snapshot.picture_url ?? identity.picture_url ?? null],
            creature_number: [source ? ecLean(source, 'creature_number') ?? 0 : 0],
            encounter_creature_private: [source ? ecLean(source, 'encounter_creature_private') ?? 0 : 0] };
    };
    // These two identities differ for inventory weapons and attack house-rule/save markers, exactly as PHP's insert arguments do.
    const { _local_used_item_id: localUsedId, ...fields } = input;
    const actor = participant(by), target = participant(to);
    const usedId = text(localUsedId ?? itemId), cached = held(usedId);
    const inventory = cachedInventoryRow(cached, usedId)
        ?? cachedInventoryRow(held(text(command.actor_id || by)), usedId);
    const templateId = text(inventoryField(inventory, 'inventory_item_id'));
    const used = (input.item_id_used_arr ? row(input.item_id_used_arr) : undefined)
        ?? (templateId ? held(templateId) ?? row(inventory?.catalog)[templateId] as Row | undefined : undefined) ?? cached;
    let usedName = text(inventoryField(inventory, 'inventory_name') || (used ? itemField(used, 'item_name') : ''));
    if (!usedName && usedId && command.schema === 'janus.action.attack.v2'
        && (usedId === text(command.weapon_id) || usedId === text(row(row(command.rolls).rider).weapon_id))) {
        const rider = row(row(command.rolls).rider), owner = participant(text(command.actor_id || by));
        const slot = usedId === text(rider.weapon_id) ? text(rider.label || 'off-hand') : 'main-hand';
        usedName = `${owner.item_name || 'Creature'}'s ${slot} weapon`;
        window.dispatchEvent(new CustomEvent('ft:unported', { detail: { mode: 'warning', reason: 'log:used-item-unresolved',
            detail: { actor_id: command.actor_id || by, encounter_id: encounterId, used_item_id: usedId } } }));
    }
    const table = row(input.random_table);
    const item = { item_id: itemId, item_name: usedName,
        item_type: used ? itemField(used, 'item_type') : '', pic_url: used?.pic_url ?? [] };
    const type2 = text(input.roll_type_2), type2Row = held(type2);
    const types = (window as unknown as { ROLLTYPE_CATALOG?: Record<string, { item_id?: string }> }).ROLLTYPE_CATALOG ?? {};
    const recipe = isResolvedRollVfxType(kind, types) || kind === 'spell_attack_missed'
        ? resolveRollVfxRecipe(itemId, held(itemId), vfxRows)
        : null;
    const roll: RollInsert['roll'] = {
        ...fields,
        roll_id: text(input.roll_id || command.operation_id), roll_i: --serial.value,
        roll_t: new Date().toISOString().slice(0, 19).replace('T', ' '), encounter_id: encounterId,
        campaign_id: text(command.campaign_id), roll_created_by_id: window.FateTide?.user?.id,
        roll_type_1: kind, roll_type_2: input.roll_type_2 === null ? null : type2, roll_type_2_name: type2Row ? itemField(type2Row, 'item_name') : '',
        roll_item_id: itemId, roll_item_arr: item, roll_item_id_item_name: item.item_name,
        roll_item_id_item_type: item.item_type, roll_item_pic_url: item.pic_url,
        roll_by: by, roll_to: to, roll_by_arr: actor, roll_to_arr: target,
        roll_by_item_name: actor.item_name, roll_by_item_type: actor.item_type, roll_by_pic_url: actor.pic_url,
        roll_to_item_name: target.item_name, roll_to_item_type: target.item_type, roll_to_pic_url: target.pic_url,
        roll_by_encounter_creature_private: actor.encounter_creature_private[0],
        roll_to_encounter_creature_private: target.encounter_creature_private[0],
        roll_private: Number(input.roll_private ?? command.roll_private ?? 0),
        roll_result: input.roll_result ?? input.result ?? 0,
        roll_mod: input.roll_mod ?? input.mod ?? 0, roll_dc: input.roll_dc ?? input.dc ?? 0,
        adv_dis_trace_json: input.adv_dis_trace_json ?? input.adv_dis_trace ?? null,
        roll_feedback: input.roll_feedback ?? input.feedback ?? null,
        roll_vfx_recipe: recipe, roll_vfx_area_cast: abilityVfxUsesAreaCast(row(recipe), used),
        roll_cast_source_id: input.roll_cast_source_id ?? input.cast_source_id ?? command.cast_source_id ?? null,
        // build_roll_arr.php reads these from this step's random_table snapshot; action sound is a separate contract.
        roll_random_table_effect: table.effect ?? null,
        roll_random_table_description: table.description ?? null,
        roll_random_table_attached_item_id: table.attached_item_id ?? null,
    };
    const motion = (abilityMotion.entries as Record<string, { motion?: string }>)[itemId.toUpperCase()]?.motion;
    if (typeof motion === 'string' && motion !== 'basic') roll.roll_motion = motion;
    const triggerId = text(input.triggered_effect_item_id), trigger = triggerId ? held(triggerId) : undefined;
    if (triggerId) Object.assign(roll, {
        triggered_effect_item_name: input.triggered_effect_item_name ?? (trigger ? itemField(trigger, 'item_name') : 'Evade'),
        triggered_effect_item_type: input.triggered_effect_item_type ?? (trigger ? itemField(trigger, 'item_type') : 'reaction_ability'),
    });
    // house-rule/concentration rows use the inciting ability; orb rows have type/name only (no used id).
    const soundItem = command.schema === 'janus.action.ability.v2' && ('house_rule' in input || kind === 'concentration_broken')
        ? held(text(command.ability_id)) : kind === 'orb_channeled' || input.item_display ? undefined : used;
    const sound = localRollSound(input, command, roll, soundItem);
    if (sound) roll.roll_sound = sound;
    else delete roll.roll_sound; // insert_rolls_inc.php omits a silent/claimed reference.
    const presentations = row(command.roll_presentations)[rollPresentationKey(roll)];
    if (Array.isArray(presentations)) {
        // Local rows are emitted in the same order as the SQL insert loop; do not consume the wire snapshot.
        const counters = presentationOffsets.get(command) ?? {};
        presentationOffsets.set(command, counters);
        const key = rollPresentationKey(roll), offset = counters[key] ?? 0;
        if (presentations[offset]) roll.roll_presentation = presentations[offset];
        counters[key] = offset + 1;
    }
    normalizeRollVfx(roll, types);
    return { type: 'roll_insert', scope: 'encounter', encounter_id: encounterId, roll };
}
